import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMatches } from '../../../shared/hooks/useMatches';
import { useMobile } from '../../../shared/hooks/useMobile';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import Button from '../../../shared/components/ui/Button';
import ReplayAppHeader from '../../../shared/components/layout/ReplayAppHeader';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useToast, ToastContainer } from '../../../shared/components/ui/Toast';
import VideoTimelineTab from './VideoTimelineTab';
import EventClipsTab from './EventClipsTab';
import VideoEditorTab from './VideoEditorTab';
import HighlightsTab from './HighlightsTab';

/**
 * Match Detail Page Component
 * 
 * Main page for viewing match videos and event clips.
 * Manages tab navigation and coordinates between Video Timeline and Event Clips tabs.
 */
const MatchDetailPage: React.FC = () => {
  const { matchId: fullMatchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobile();
  
  // Helper function to get tab from hash
  const getTabFromHash = (hash: string): 'video-timeline' | 'event-clips' | 'video-editor' | 'highlights' => {
    const cleanHash = hash.replace('#', '');
    if (cleanHash === 'videos' || cleanHash === 'video-timeline') return 'video-timeline';
    if (cleanHash === 'timeline' || cleanHash === 'event-clips') return 'event-clips';
    if (cleanHash === 'editor' || cleanHash === 'video-editor') return 'video-editor';
    if (cleanHash === 'highlights') return 'highlights';
    return 'video-timeline'; // default
  };
  
  const [activeTab, setActiveTab] = useState<'video-timeline' | 'event-clips' | 'video-editor' | 'highlights'>(() => 
    getTabFromHash(location.hash)
  );
  const [pendingSeek, setPendingSeek] = useState<{ videoId: string; time: number } | null>(null);
  const [highlightToLoadId, setHighlightToLoadId] = useState<string | null>(null);
  const [highlightsRefreshKey, setHighlightsRefreshKey] = useState(0);
  const { toasts, showToast, removeToast } = useToast();
  
  // Extract actual matchId from the URL
  // Handle both old format (with -videos, -details, -timeline) and new format (clean)
  const matchId = fullMatchId?.replace(/-videos|-details|-timeline/, '') || '';
  
  const { matches, loading: matchesLoading, error: matchesError } = useMatches();
  const currentMatch = matches?.find(match => match.id === matchId);

  // Handle hash-based tab detection and query params for highlight loading
  useEffect(() => {
    const newTab = getTabFromHash(location.hash);
    
    // Update tab if hash changed
    setActiveTab((currentTab) => {
      // Only update if different to avoid unnecessary re-renders
      return newTab !== currentTab ? newTab : currentTab;
    });
    
    // Check for highlight query param
    const searchParams = new URLSearchParams(location.search);
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightToLoadId(highlightId);
      // Switch to editor tab if highlight is provided
      setActiveTab('video-editor');
    } else {
      // Clear highlight if query param is removed
      setHighlightToLoadId(null);
    }
  }, [location.hash, location.search]); // Only react to hash/search changes, not activeTab


  const handleBackClick = () => {
    const orgIdFromPath = extractOrgIdFromPath(location.pathname);
    const orgId = orgIdFromPath || 'default';
    navigate(`/o/${orgId}/replay`);
  };

  // Handle event click from Event Clips tab - switch to Video Timeline and seek
  const handleEventClick = (videoTimeSeconds: number, videoId: string) => {
    setPendingSeek({ videoId, time: videoTimeSeconds });
    setActiveTab('video-timeline');
    window.history.replaceState(null, '', `${location.pathname}#video-timeline`);
  };

  // Handle tab navigation
  const handleTabChange = (tabId: 'video-timeline' | 'event-clips' | 'video-editor' | 'highlights') => {
    setActiveTab(tabId);
    // Update URL hash without causing page reload
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  const handleOpenHighlight = (highlightId: string) => {
    setHighlightToLoadId(highlightId);
    handleTabChange('video-editor');
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'video-timeline':
        return (
          <VideoTimelineTab
            matchId={matchId}
            isActive={activeTab === 'video-timeline'}
            pendingSeek={pendingSeek}
            onSeekComplete={() => setPendingSeek(null)}
          />
        );
      case 'event-clips':
        return (
          <EventClipsTab
            matchId={matchId}
            isActive={activeTab === 'event-clips'}
            onEventClick={handleEventClick}
          />
        );
      case 'video-editor':
        return (
          <VideoEditorTab
            matchId={matchId}
            isActive={activeTab === 'video-editor'}
            highlightToLoadId={highlightToLoadId}
            onHighlightLoaded={() => setHighlightToLoadId(null)}
            onHighlightSaved={(highlightId, action) => {
              setHighlightsRefreshKey((key) => key + 1);
              // If creating new, redirect to highlights tab
              // If updating existing, stay on editor tab
              if (action === 'create') {
                handleTabChange('highlights');
              } else {
                setHighlightToLoadId(highlightId || null);
              }
            }}
            onToast={showToast}
          />
        );
      case 'highlights':
        return (
          <HighlightsTab
            matchId={matchId}
            isActive={activeTab === 'highlights'}
            refreshKey={highlightsRefreshKey}
            onOpenHighlight={handleOpenHighlight}
            onHighlightsChanged={() => setHighlightsRefreshKey((key) => key + 1)}
            onToast={showToast}
          />
        );
      default:
        return (
          <VideoTimelineTab
            matchId={matchId}
            isActive={true}
          />
        );
    }
  };

  if (matchesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (matchesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Matches</h3>
          <p className="text-gray-500 mb-4">{matchesError}</p>
          <Button onClick={handleBackClick} variant="outline">
            Back to Replay
          </Button>
        </div>
      </div>
    );
  }

  // Show not found state if match is not found
  if (!currentMatch) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.57M15 6.343A7.962 7.962 0 0112 5c-2.34 0-4.29 1.009-5.824 2.57" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Match Not Found</h3>
          <p className="text-gray-500 mb-4">The requested match could not be found.</p>
          <p className="text-xs text-gray-400 mb-4">Match ID: {matchId}</p>
          <Button onClick={handleBackClick} variant="outline">
            Back to Replay
          </Button>
        </div>
      </div>
    );
  }

  // Tab configuration
  const tabs = [
    { id: 'video-timeline', label: 'Video Timeline', icon: '🎥' },
    { id: 'event-clips', label: 'Event Clips', icon: '⚡' },
    { id: 'video-editor', label: 'Video Editor', icon: '✂️' },
    { id: 'highlights', label: 'Highlights', icon: '⭐' },
  ] as const;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Tabs */}
      <ReplayAppHeader
        isMobile={isMobile}
        leftContent={
          !isMobile ? (
            <Button
              onClick={handleBackClick}
              variant="ghost"
              size="md"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              }
              iconPosition="left"
            >
              Back to Matches
            </Button>
          ) : null
        }
        centerContent={
          <div className={`flex ${isMobile ? 'space-x-6' : 'space-x-8'} ${isMobile ? 'justify-center' : ''}`}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-2 px-2 border-b-2 font-medium ${isMobile ? 'text-sm' : 'text-sm'} flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'} min-h-44 ${
                  activeTab === tab.id
                    ? 'border-gray-400 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                title={isMobile ? tab.label : undefined}
              >
                <span className={isMobile ? 'text-lg' : ''}>{tab.icon}</span>
                {!isMobile && <span>{tab.label}</span>}
              </button>
            ))}
          </div>
        }
        rightContent={
          <div className="flex items-center space-x-2">
            {/* Go to Match Center Button */}
            <Button
              onClick={() => {
                const orgIdFromPath = extractOrgIdFromPath(location.pathname);
                if (orgIdFromPath) {
                  navigate(`/o/${orgIdFromPath}/reports/${matchId}#details`);
                } else {
                  navigate(`/reports/${matchId}#details`);
                }
              }}
              variant="secondary"
              size="md"
              className="min-w-[120px] transition-all duration-200"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
            >
              {'Report'}
            </Button>
          </div>
        }
      />

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6">
        {renderTabContent()}
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default MatchDetailPage;
